import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import {
  Search,
  ArrowUp,
  ArrowDown,
  Pencil,
  Copy,
  MoreHorizontal,
  Power,
  PowerOff,
  Library,
  NotebookPen,
  Layers,
  Sparkles,
  Plus,
  Shield,
} from 'lucide-react';
import { useITeachConfig } from '../_context/PresetContext';
import ApiService from '../_services/ApiService';
import { SkeletonLoader } from '../_components/skeleton-loader/SkeletonLoader';
import './learning-modules.css';

interface LearningModule {
  id: number;
  name: string;
  code: string;
  typeId: number;
  subTypeId: number;
  instructorId: number;
  creationDate: string;
  lastModifiedDate: string;
  status: number;
  analyticsDashboard: number;
  lessonPlanTypeId: number;
  lessonPlanType?: 'learning_module' | 'lesson_plan';
}


const TYPE_LABELS: Record<string, string> = {
  lesson_plan: 'Lesson Plan',
  learning_module: 'Learning Module',
};

const LearningModules: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { config } = useITeachConfig();
  const [modules, setModules] = useState<LearningModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(() => {
    // Allow deep-links like /learning-modules?q=<course name> to pre-fill the
    // search box. Used by the governance console to drop a reviewer onto the
    // modules list filtered by the course they were just looking at.
    try {
      return new URLSearchParams(location.search).get('q') || '';
    } catch { return ''; }
  });
  const [error, setError] = useState<string | null>(null);

  // Filter state — restore from localStorage if available
  const [filters, setFilters] = useState(() => {
    const defaults = {
      status: '1',              // '1'=active, '0'=inactive, ''=all
      analyticsDashboard: '',   // '1'=enabled, '0'=disabled, ''=all
      type: '',                 // 'learning_module', 'lesson_plan', ''=all
      sortBy: 'name',           // 'name', 'id', 'createdDate'
      sortOrder: 'asc',         // 'asc', 'desc'
    };
    try {
      const saved = localStorage.getItem('lm-list-filters');
      if (saved) return { ...defaults, ...JSON.parse(saved) };
    } catch { /* ignore */ }
    return defaults;
  });

  // Status toggle state
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [toggleTarget, setToggleTarget] = useState<LearningModule | null>(null);

  // Row action menu (kebab)
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Clone state
  const [cloneTarget, setCloneTarget] = useState<LearningModule | null>(null);
  const [cloneMode, setCloneMode] = useState<'full' | 'structure'>('full');
  const [cloneName, setCloneName] = useState('');
  const [cloning, setCloning] = useState(false);
  // Result/message modal (success or error) — Modal instead of window.alert (see CLAUDE.md)
  const [message, setMessage] = useState<{ title: string; body: string } | null>(null);

  useEffect(() => {
    fetchModules();
    try { localStorage.setItem('lm-list-filters', JSON.stringify(filters)); } catch { /* ignore */ }
  }, [filters]);

  // Close kebab menu on outside click
  useEffect(() => {
    if (menuOpenId === null) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpenId]);

  const fetchModules = async () => {
    try {
      setLoading(true);
      setError(null);
      // Build params, only include non-empty filter values
      const params: { status?: string; analyticsDashboard?: string; sortBy?: string; sortOrder?: string } = {};
      if (filters.status) params.status = filters.status;
      if (filters.analyticsDashboard) params.analyticsDashboard = filters.analyticsDashboard;
      if (filters.sortBy) params.sortBy = filters.sortBy;
      if (filters.sortOrder) params.sortOrder = filters.sortOrder;

      const response = await ApiService.getLessonPlans(params);
      setModules(response.data || []);
    } catch (err: any) {
      console.error('Error fetching learning modules:', err);
      setError(err.message || 'Failed to load learning modules');
    } finally {
      setLoading(false);
    }
  };

  const filteredModules = modules.filter((module) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      module.name?.toLowerCase().includes(query) ||
      module.code?.toLowerCase().includes(query) ||
      module.id.toString().includes(query);

    const matchesType = !filters.type || module.lessonPlanType === filters.type;

    return matchesSearch && matchesType;
  });

  // Summary stats (over the visible/filtered set)
  const stats = useMemo(() => {
    const learningCount = filteredModules.filter((m) => m.lessonPlanType === 'learning_module').length;
    const lessonCount = filteredModules.filter((m) => m.lessonPlanType === 'lesson_plan').length;
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const updatedThisWeek = filteredModules.filter((m) => {
      const t = new Date(m.lastModifiedDate).getTime();
      return !isNaN(t) && t >= sevenDaysAgo;
    }).length;
    return { learningCount, lessonCount, updatedThisWeek };
  }, [filteredModules]);

  const handleView = (id: number) => {
    history.push(`/lm-builder/${id}?mode=view`);
  };

  const handleEdit = (id: number) => {
    history.push(`/lm-builder/${id}?mode=edit`);
  };

  // Derive create button label from config
  const restrictTo = config.lmBuilder?.lessonPlanTypeRestrictTo;
  const createLabel = restrictTo
    ? `Create ${TYPE_LABELS[restrictTo] || restrictTo}`
    : 'Create New Module';

  const confirmToggleStatus = async () => {
    if (!toggleTarget) return;
    const newStatus = toggleTarget.status === 1 ? 0 : 1;
    try {
      setTogglingId(toggleTarget.id);
      await ApiService.updateLessonPlanStatus(toggleTarget.id, newStatus);
      setToggleTarget(null);
      await fetchModules();
    } catch (err: any) {
      console.error('Error toggling status:', err);
      setMessage({
        title: 'Update failed',
        body: err?.message || 'Failed to update status',
      });
    } finally {
      setTogglingId(null);
    }
  };

  const openCloneDialog = (module: LearningModule) => {
    setCloneMode('full');
    setCloneName(`Copy of ${module.name || 'Untitled'}`);
    setCloneTarget(module);
  };

  const handleClone = async () => {
    if (!cloneTarget) return;
    try {
      setCloning(true);
      const res = await ApiService.cloneLessonPlan(cloneTarget.id, cloneMode, cloneName.trim());
      const created: any = res.data || {};
      setCloneTarget(null);
      await fetchModules();
      setMessage({
        title: 'Clone created',
        body:
          `"${created.name || 'Copy'}" was created successfully` +
          (cloneMode === 'structure'
            ? ' as a structure-only copy — its sections are empty and ready for new content.'
            : ' with all sections, resources and tests.'),
      });
    } catch (err: any) {
      console.error('Error cloning module:', err);
      setMessage({
        title: 'Clone failed',
        body: err?.response?.data?.message || err?.message || 'Unknown error',
      });
    } finally {
      setCloning(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isLearningModule = (m: LearningModule) => m.lessonPlanType === 'learning_module';

  return (
    <div className="learning-modules-container">
      <div className="learning-modules-header">
        <div className="header-title-wrap">
          <h1>Learning Modules &amp; Lesson Plans</h1>
          {!loading && !error && (
            <div className="header-stats">
              <span className="stat-chip stat-chip-total">
                <Layers size={13} />
                {filteredModules.length}
                <span className="stat-label">total</span>
              </span>
              <span className="stat-chip stat-chip-lm">
                <Library size={13} />
                {stats.learningCount}
                <span className="stat-label">learning modules</span>
              </span>
              <span className="stat-chip stat-chip-lp">
                <NotebookPen size={13} />
                {stats.lessonCount}
                <span className="stat-label">lesson plans</span>
              </span>
              {stats.updatedThisWeek > 0 && (
                <span className="stat-chip stat-chip-fresh">
                  <Sparkles size={13} />
                  {stats.updatedThisWeek}
                  <span className="stat-label">updated this week</span>
                </span>
              )}
            </div>
          )}
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => history.push('/lcs-access-requests')}>
            <Shield size={15} style={{ marginRight: 6 }} />
            LCS Access Requests
          </button>
          <button className="btn-primary" onClick={() => history.push('/lm-builder')}>
            <Plus size={16} style={{ marginRight: 4 }} />
            {createLabel}
          </button>
        </div>
      </div>

      <div className="learning-modules-filters">
        <div className="search-wrap">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, code, or ID…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="1">Active</option>
          <option value="0">Inactive</option>
        </select>
        <select
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          className="filter-select"
        >
          <option value="">All Types</option>
          <option value="learning_module">Learning Module</option>
          <option value="lesson_plan">Lesson Plan</option>
        </select>
        <select
          value={filters.analyticsDashboard}
          onChange={(e) => setFilters({ ...filters, analyticsDashboard: e.target.value })}
          className="filter-select"
        >
          <option value="">All Analytics</option>
          <option value="1">Analytics Enabled</option>
          <option value="0">Analytics Disabled</option>
        </select>
        <div className="sort-group">
          <select
            value={filters.sortBy}
            onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
            className="filter-select"
          >
            <option value="name">Sort by Name</option>
            <option value="id">Sort by ID</option>
            <option value="createdDate">Sort by Date</option>
          </select>
          <button
            className="sort-order-btn"
            onClick={() => setFilters({ ...filters, sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc' })}
            title={filters.sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          >
            {filters.sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
          </button>
        </div>
      </div>

      {loading && (
        <SkeletonLoader type="table" />
      )}

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
          <button onClick={fetchModules} style={{ marginLeft: 12 }}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && filteredModules.length === 0 && (
        <div className="empty-message">
          {searchQuery ? (
            <>No modules found matching "{searchQuery}"</>
          ) : (
            <>
              No learning modules found.{' '}
              <button onClick={() => history.push('/lm-builder')} className="link-button">
                Create your first module
              </button>
            </>
          )}
        </div>
      )}

      {!loading && !error && filteredModules.length > 0 && (
        <div className="learning-modules-table-container">
          <table className="learning-modules-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Type</th>
                <th>Last Modified</th>
                <th className="actions-col"></th>
              </tr>
            </thead>
            <tbody>
              {filteredModules.map((module) => {
                const lm = isLearningModule(module);
                const isInactive = module.status !== 1;
                return (
                  <tr
                    key={module.id}
                    className={`clickable-row ${isInactive ? 'row-inactive' : ''}`}
                    onClick={() => handleView(module.id)}
                  >
                    <td className="name-cell">
                      <span className={`type-dot ${lm ? 'type-dot-lm' : 'type-dot-lp'}`} />
                      <div className="name-inner">
                        <div className="name-line">
                          <span className="name-text">{module.name || 'Untitled'}</span>
                          {isInactive && <span className="inactive-pill">Inactive</span>}
                        </div>
                        <span className="id-badge">#{module.id}</span>
                      </div>
                    </td>
                    <td>
                      <code className="code-chip" title={module.code || 'N/A'}>
                        {module.code || '—'}
                      </code>
                    </td>
                    <td>
                      <span className={`type-badge ${lm ? 'type-badge-lm' : 'type-badge-lp'}`}>
                        {lm ? <Library size={11} /> : <NotebookPen size={11} />}
                        {lm ? 'Learning Module' : 'Lesson Plan'}
                      </span>
                    </td>
                    <td className="dates-cell">
                      <div className="date-modified">{formatDate(module.lastModifiedDate)}</div>
                      <div className="date-created">Created {formatDate(module.creationDate)}</div>
                    </td>
                    <td className="actions-cell">
                      <div className="row-actions">
                        <button
                          className="row-icon-btn"
                          title="Edit"
                          onClick={(e) => { e.stopPropagation(); handleEdit(module.id); }}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          className="row-icon-btn"
                          title="Clone"
                          onClick={(e) => { e.stopPropagation(); openCloneDialog(module); }}
                        >
                          <Copy size={15} />
                        </button>
                        <div className="row-menu-wrap" ref={menuOpenId === module.id ? menuRef : null}>
                          <button
                            className="row-icon-btn"
                            title="More"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuOpenId(menuOpenId === module.id ? null : module.id);
                            }}
                          >
                            <MoreHorizontal size={16} />
                          </button>
                          {menuOpenId === module.id && (
                            <div className="row-menu" onClick={(e) => e.stopPropagation()}>
                              <button
                                className={`row-menu-item ${module.status === 1 ? 'danger' : ''}`}
                                disabled={togglingId === module.id}
                                onClick={() => {
                                  setMenuOpenId(null);
                                  setToggleTarget(module);
                                }}
                              >
                                {module.status === 1 ? (
                                  <>
                                    <PowerOff size={14} /> Disable module
                                  </>
                                ) : (
                                  <>
                                    <Power size={14} /> Enable module
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="learning-modules-footer">
        <span className="count-text">
          Showing {filteredModules.length} of {modules.length} module(s)
        </span>
      </div>

      {/* Clone scope chooser */}
      <Modal show={!!cloneTarget} onHide={() => !cloning && setCloneTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Clone "{cloneTarget?.name || 'Module'}"</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="clone-name" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
              New module name
            </label>
            <input
              id="clone-name"
              type="text"
              className="form-control"
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              placeholder="Enter a name for the cloned module"
              autoFocus
            />
          </div>

          <p style={{ color: 'var(--text-body)', marginBottom: 16 }}>
            Choose what to copy into the new module. The original is never changed,
            and shared resources/tests are referenced — not duplicated.
          </p>

          <label
            className={`clone-option ${cloneMode === 'full' ? 'clone-option-active' : ''}`}
            style={cloneOptionStyle(cloneMode === 'full')}
          >
            <input
              type="radio"
              name="cloneMode"
              checked={cloneMode === 'full'}
              onChange={() => setCloneMode('full')}
              style={{ marginRight: 10, marginTop: 4 }}
            />
            <span>
              <strong>Full Copy</strong>
              <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 13 }}>
                Copies all sections together with their resources and tests. A ready-to-use duplicate.
              </span>
            </span>
          </label>

          <label
            className={`clone-option ${cloneMode === 'structure' ? 'clone-option-active' : ''}`}
            style={cloneOptionStyle(cloneMode === 'structure')}
          >
            <input
              type="radio"
              name="cloneMode"
              checked={cloneMode === 'structure'}
              onChange={() => setCloneMode('structure')}
              style={{ marginRight: 10, marginTop: 4 }}
            />
            <span>
              <strong>Structure Only</strong>
              <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 13 }}>
                Copies just the section/chapter outline. Sections come out empty — a blank template to fill in.
              </span>
            </span>
          </label>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCloneTarget(null)} disabled={cloning}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleClone} disabled={cloning || !cloneName.trim()}>
            {cloning ? 'Cloning…' : 'Clone'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toggle status confirm */}
      <Modal show={!!toggleTarget} onHide={() => !togglingId && setToggleTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {toggleTarget?.status === 1 ? 'Disable module?' : 'Enable module?'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {toggleTarget?.status === 1 ? (
            <>
              <p style={{ marginBottom: 8 }}>
                <strong>"{toggleTarget?.name || 'Untitled'}"</strong> will be hidden from
                students and removed from active course lists.
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 0 }}>
                You can re-enable it anytime from the filter view.
              </p>
            </>
          ) : (
            <p style={{ marginBottom: 0 }}>
              Re-enable <strong>"{toggleTarget?.name || 'Untitled'}"</strong> so it appears in
              student views again.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setToggleTarget(null)} disabled={togglingId !== null}>
            Cancel
          </Button>
          <Button
            variant={toggleTarget?.status === 1 ? 'danger' : 'primary'}
            onClick={confirmToggleStatus}
            disabled={togglingId !== null}
          >
            {togglingId !== null
              ? 'Updating…'
              : toggleTarget?.status === 1
                ? 'Disable'
                : 'Enable'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Result / error message */}
      <Modal show={!!message} onHide={() => setMessage(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{message?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{message?.body}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setMessage(null)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// Inline style helper for the clone scope option cards
const cloneOptionStyle = (active: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'flex-start',
  padding: '12px 14px',
  marginBottom: 10,
  border: `1.5px solid ${active ? '#9333ea' : '#e2e8f0'}`,
  borderRadius: 8,
  cursor: 'pointer',
  background: active ? 'rgba(147, 51, 234, 0.06)' : '#fff',
  transition: 'all 0.15s ease',
});

export default LearningModules;
